import React from 'react';

// Swiss line-art empty-state illustrations — navy strokes, teal accent, ARIA green pulse
const NAVY = '#0D1B2A', TEAL = '#00B4D8', GREEN = '#1D9E75', SLATE = '#64748B', FILL = '#F5F7FA', AMBER = '#E8A020';

const Frame = ({ children, width }) => (
  <svg viewBox="0 0 200 120" style={{ width, height: 'auto' }} fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <g opacity="0.5">
      {[20, 60, 100, 140, 180].map(x => [20, 60, 100].map(y => (
        <circle key={`${x}-${y}`} cx={x} cy={y} r="1" fill={SLATE} opacity="0.35" />
      )))}
    </g>
    {children}
    <circle cx="178" cy="16" r="3.5" fill={GREEN}>
      <animate attributeName="opacity" values="1;0.35;1" dur="2.4s" repeatCount="indefinite" />
    </circle>
    <circle cx="178" cy="16" r="6.5" stroke={GREEN} strokeWidth="1" opacity="0.35" />
  </svg>
);

const Doc = ({ x, y, w = 44, h = 56, accent }) => (
  <g>
    <rect x={x} y={y} width={w} height={h} rx="2" fill="white" stroke={NAVY} strokeWidth="1.5" />
    <line x1={x + 8} y1={y + 12} x2={x + w - 8} y2={y + 12} stroke={SLATE} strokeWidth="1.5" />
    <line x1={x + 8} y1={y + 20} x2={x + w - 14} y2={y + 20} stroke={SLATE} strokeWidth="1.5" opacity="0.6" />
    <line x1={x + 8} y1={y + 28} x2={x + w - 10} y2={y + 28} stroke={SLATE} strokeWidth="1.5" opacity="0.6" />
    {accent && <circle cx={x + w - 12} cy={y + h - 12} r="7" fill="none" stroke={TEAL} strokeWidth="1.5" />}
  </g>
);

export const ART = {
  policies: ({ width }) => (
    <Frame width={width}>
      <rect x="66" y="36" width="44" height="56" rx="2" fill={FILL} stroke={SLATE} strokeWidth="1.2" transform="rotate(-4 88 64)" />
      <Doc x={78} y={32} accent />
      <path d="M116 78 l5 5 l9 -10" stroke={GREEN} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </Frame>
  ),
  submissions: ({ width }) => (
    <Frame width={width}>
      <rect x="48" y="30" width="52" height="64" rx="3" fill="white" stroke={NAVY} strokeWidth="1.5" />
      <rect x="62" y="24" width="24" height="10" rx="2" fill={FILL} stroke={NAVY} strokeWidth="1.5" />
      <line x1="56" y1="48" x2="92" y2="48" stroke={SLATE} strokeWidth="1.5" />
      <line x1="56" y1="58" x2="86" y2="58" stroke={SLATE} strokeWidth="1.5" opacity="0.6" />
      <line x1="56" y1="68" x2="90" y2="68" stroke={SLATE} strokeWidth="1.5" opacity="0.6" />
      <path d="M104 62 h28" stroke={TEAL} strokeWidth="1.5" strokeDasharray="3 3" />
      <path d="M128 57 l6 5 l-6 5" stroke={TEAL} strokeWidth="1.5" fill="none" strokeLinecap="round" />
      <circle cx="150" cy="62" r="13" fill="white" stroke={NAVY} strokeWidth="1.5" />
      <text x="150" y="66" textAnchor="middle" fontSize="9" fontFamily="monospace" fill={TEAL}>AI</text>
    </Frame>
  ),
  claims: ({ width }) => (
    <Frame width={width}>
      <path d="M100 26 l26 9 v22 c0 18 -12 28 -26 34 c-14 -6 -26 -16 -26 -34 v-22 z" fill="white" stroke={NAVY} strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M89 58 l8 8 l15 -17" stroke={TEAL} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      <line x1="52" y1="98" x2="148" y2="98" stroke={SLATE} strokeWidth="1.2" opacity="0.5" />
    </Frame>
  ),
  renewals: ({ width }) => (
    <Frame width={width}>
      <path d="M68 60 a32 32 0 0 1 55 -22" stroke={NAVY} strokeWidth="1.5" fill="none" strokeLinecap="round" />
      <path d="M132 60 a32 32 0 0 1 -55 22" stroke={TEAL} strokeWidth="1.5" fill="none" strokeLinecap="round" />
      <path d="M118 30 h7 v7" stroke={NAVY} strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M82 90 h-7 v-7" stroke={TEAL} strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      <rect x="86" y="46" width="28" height="30" rx="2" fill="white" stroke={NAVY} strokeWidth="1.5" />
      <line x1="92" y1="56" x2="108" y2="56" stroke={SLATE} strokeWidth="1.5" />
      <line x1="92" y1="63" x2="104" y2="63" stroke={SLATE} strokeWidth="1.5" opacity="0.6" />
    </Frame>
  ),
  portfolio: ({ width }) => (
    <Frame width={width}>
      <line x1="100" y1="38" x2="62" y2="76" stroke={SLATE} strokeWidth="1.2" />
      <line x1="100" y1="38" x2="138" y2="76" stroke={SLATE} strokeWidth="1.2" />
      <line x1="100" y1="38" x2="100" y2="84" stroke={SLATE} strokeWidth="1.2" />
      <circle cx="100" cy="38" r="11" fill="white" stroke={NAVY} strokeWidth="1.5" />
      <circle cx="62" cy="78" r="9" fill="white" stroke={TEAL} strokeWidth="1.5" />
      <circle cx="100" cy="86" r="9" fill="white" stroke={NAVY} strokeWidth="1.5" />
      <circle cx="138" cy="78" r="9" fill="white" stroke={GREEN} strokeWidth="1.5" />
    </Frame>
  ),
  compliance: ({ width }) => (
    <Frame width={width}>
      <line x1="100" y1="30" x2="100" y2="88" stroke={NAVY} strokeWidth="1.5" />
      <line x1="66" y1="42" x2="134" y2="42" stroke={NAVY} strokeWidth="1.5" />
      <path d="M56 66 a10 10 0 0 0 20 0 l-10 -24 z" fill="white" stroke={TEAL} strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M124 66 a10 10 0 0 0 20 0 l-10 -24 z" fill="white" stroke={NAVY} strokeWidth="1.5" strokeLinejoin="round" />
      <line x1="84" y1="92" x2="116" y2="92" stroke={NAVY} strokeWidth="1.5" />
    </Frame>
  ),
  finance: ({ width }) => (
    <Frame width={width}>
      <rect x="52" y="40" width="18" height="52" fill="white" stroke={NAVY} strokeWidth="1.5" />
      <rect x="78" y="54" width="18" height="38" fill="white" stroke={TEAL} strokeWidth="1.5" />
      <rect x="104" y="64" width="18" height="28" fill="white" stroke={SLATE} strokeWidth="1.5" />
      <rect x="130" y="72" width="18" height="20" fill={FILL} stroke={GREEN} strokeWidth="1.5" />
      <path d="M56 32 q40 -14 88 30" stroke={AMBER} strokeWidth="1.2" strokeDasharray="3 3" fill="none" />
    </Frame>
  ),
  clients: ({ width }) => (
    <Frame width={width}>
      <circle cx="84" cy="46" r="12" fill="white" stroke={NAVY} strokeWidth="1.5" />
      <path d="M60 92 a24 20 0 0 1 48 0" fill="white" stroke={NAVY} strokeWidth="1.5" />
      <circle cx="122" cy="52" r="9" fill={FILL} stroke={TEAL} strokeWidth="1.5" />
      <path d="M104 92 a19 16 0 0 1 37 0" fill={FILL} stroke={TEAL} strokeWidth="1.5" />
    </Frame>
  ),
  complaints: ({ width }) => (
    <Frame width={width}>
      <path d="M60 36 h64 a4 4 0 0 1 4 4 v30 a4 4 0 0 1 -4 4 h-40 l-12 12 v-12 h-12 a4 4 0 0 1 -4 -4 v-30 a4 4 0 0 1 4 -4 z" fill="white" stroke={NAVY} strokeWidth="1.5" strokeLinejoin="round" />
      <line x1="70" y1="50" x2="116" y2="50" stroke={SLATE} strokeWidth="1.5" />
      <line x1="70" y1="60" x2="106" y2="60" stroke={SLATE} strokeWidth="1.5" opacity="0.6" />
      <circle cx="136" cy="76" r="13" fill="white" stroke={TEAL} strokeWidth="1.5" />
      <path d="M136 69 v8 l5 4" stroke={TEAL} strokeWidth="1.5" fill="none" strokeLinecap="round" />
    </Frame>
  ),
  referrals: ({ width }) => (
    <Frame width={width}>
      <rect x="56" y="28" width="88" height="16" rx="2" fill="white" stroke={SLATE} strokeWidth="1.2" />
      <rect x="56" y="52" width="88" height="16" rx="2" fill="white" stroke={SLATE} strokeWidth="1.2" />
      <rect x="56" y="76" width="88" height="16" rx="2" fill="white" stroke={SLATE} strokeWidth="1.2" />
      <path d="M62 36 l3 3 l6 -7" stroke={GREEN} strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M62 60 l3 3 l6 -7" stroke={GREEN} strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M62 84 l3 3 l6 -7" stroke={GREEN} strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      <line x1="78" y1="36" x2="132" y2="36" stroke={SLATE} strokeWidth="1.5" opacity="0.6" />
      <line x1="78" y1="60" x2="126" y2="60" stroke={SLATE} strokeWidth="1.5" opacity="0.6" />
      <line x1="78" y1="84" x2="130" y2="84" stroke={SLATE} strokeWidth="1.5" opacity="0.6" />
    </Frame>
  ),
  dashboard: ({ width }) => (
    <Frame width={width}>
      <polyline points="52,86 76,68 96,76 120,50 148,38" stroke={TEAL} strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      {[[52, 86], [76, 68], [96, 76], [120, 50], [148, 38]].map(([x, y]) => (
        <circle key={x} cx={x} cy={y} r="3" fill="white" stroke={NAVY} strokeWidth="1.5" />
      ))}
      <line x1="48" y1="96" x2="152" y2="96" stroke={SLATE} strokeWidth="1.2" opacity="0.5" />
    </Frame>
  ),
  search: ({ width }) => (
    <Frame width={width}>
      <rect x="54" y="40" width="52" height="40" rx="2" fill={FILL} stroke={SLATE} strokeWidth="1.2" />
      <circle cx="112" cy="58" r="18" fill="white" stroke={NAVY} strokeWidth="1.5" />
      <line x1="125" y1="71" x2="140" y2="86" stroke={NAVY} strokeWidth="2" strokeLinecap="round" />
      <line x1="104" y1="54" x2="120" y2="54" stroke={TEAL} strokeWidth="1.5" />
      <line x1="104" y1="62" x2="116" y2="62" stroke={TEAL} strokeWidth="1.5" opacity="0.6" />
    </Frame>
  ),
};

export const EmptyArt = ({ type, width = 176 }) => {
  const Art = ART[type] || ART.dashboard;
  return <Art width={width} />;
};

export default EmptyArt;
